import React, { useState } from "react";
import { observer } from "mobx-react";
import SequenceUtils from "Utils/SequenceUtils";
import Button from "Components/common/Button";
import InputTextBox from "Components/common/InputTextBox";
import AccountAutoCompleteDropdown from "Components/common/AccountAutoCompleteDropdown";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import { addAccountPopupState } from "Pages/Dashboard/Accounts/AddAccounts/AddAccountPopup";
import { contactAccountUpdateState } from "./ContactAccountUpdateState";
import { toasterState } from "Components/common/toaster";

// Mirrors BulkContactStageChangePopup.js — same shape, an account picker
// (AccountAutoCompleteDropdown) instead of a stage radio-list.
const ChangeContactAccountPopup = observer((props = {}) => {
  const { clearSelectedContacts = () => {}, reloadContactsPage = () => {} } =
    props || {};
  const {
    contactCount = 0,
    contactIds = [],
    allContactsSelectedInView = false,
    searchFilter = {}
  } = contactAccountUpdateState?.contactAccountUpdateObj || {};

  const [updateLoader, setUpdateLoader] = useState(false);
  const [accountInput, setAccountInput] = useState("");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [focusTrigger, setFocusTrigger] = useState(0);

  const onAccountInputChange = (id = "", val = "") => {
    selectedAccount && setSelectedAccount(null);
    setAccountInput(val);
  };

  const onAccountInputFocus = () => {
    setFocusTrigger(c => c + 1);
  };

  const onAccountSelected = (account = {}) => {
    setSelectedAccount(account);
    setAccountInput(account?.name || account?.domain || "");
  };

  const openCreateAccountPopup = () => {
    addAccountPopupState.setMode("single");
    addAccountPopupState.setOnDone(createdAccount => {
      if (createdAccount?.id) {
        onAccountSelected(createdAccount);
      }
    });
    addAccountPopupState.setShowPopup(true);
  };

  const changeAccountAction = async () => {
    if (!selectedAccount?.id) return;
    setUpdateLoader(true);
    const response = await contactAccountUpdateState.changeContactsAccount(
      selectedAccount.id,
      contactIds,
      allContactsSelectedInView,
      contactCount,
      searchFilter
    );
    const { status = "", data = {} } = response || {};
    const {
      background = false,
      activeJob = false,
      bulkOperationResult = {}
    } = data || {};
    const { success = 0, failed = 0 } = bulkOperationResult || {};
    if (+status === 200) {
      if (activeJob) {
        SequenceUtils.showActiveJobPopupForBulkActions();
      } else if (background) {
        confirmationPopupState.setPopupValues({
          content:
            "Contacts are currently being moved. This process may take a few to several minutes, depending on the total number of contacts selected.",
          needCancelBtn: false,
          actionBtnText: "Ok",
          callback: () => confirmationPopupState.setShowPopup(false)
        });
        confirmationPopupState.setShowPopup(true);
        reloadContactsPage();
      } else if (success > 0) {
        confirmationPopupState.setPopupValues({
          content: `${success} contact${success > 1 ? "s" : ""} ${
            success > 1 ? "have" : "has"
          } been moved to ${selectedAccount?.name || "the selected account"}`,
          needCancelBtn: false,
          actionBtnText: "Ok",
          callback: () => confirmationPopupState.setShowPopup(false)
        });
        confirmationPopupState.setShowPopup(true);
        reloadContactsPage();
      } else if (failed > 0) {
        confirmationPopupState.setPopupValues({
          content: `Failed to move ${failed} contact${
            failed > 1 ? "s" : ""
          } — the selected account may no longer be available`,
          needCancelBtn: false,
          actionBtnText: "Ok",
          callback: () => confirmationPopupState.setShowPopup(false)
        });
        confirmationPopupState.setShowPopup(true);
      }
    } else {
      toasterState.setToastMsg(
        "Something went wrong. Please try again later",
        "fail"
      );
    }
    clearSelectedContacts();
    contactAccountUpdateState.setShowChangeAccountPopup(false);
    contactAccountUpdateState.resetContactAccountUpdateObj();
    setUpdateLoader(false);
  };

  const closePopup = () => {
    contactAccountUpdateState.setShowChangeAccountPopup(false);
    contactAccountUpdateState.resetContactAccountUpdateObj();
  };

  const getSelectedCount = () => {
    return allContactsSelectedInView ? "All" : contactCount;
  };

  const buttonList = [
    {
      id: "cancel",
      name: "cancel",
      btnText: "Cancel",
      type: "button",
      className: "cancelButton",
      click: closePopup
    },
    {
      id: "update",
      name: "update",
      btnText: "Move",
      type: "button",
      className: `updateButton ${updateLoader ? "loading" : ""}`,
      click: changeAccountAction,
      loader: updateLoader,
      disabled: !selectedAccount?.id
    }
  ];

  return (
    <div className="bulkContactStageChangeComp changeAccountPopupComp">
      <div className="card animated customzoomIn">
        <div className="cardHeader">
          <div className="titleContainer">
            <div className="title">Change Account</div>
            <div className="contactSelectedCont">
              {`${getSelectedCount()} contact${
                getSelectedCount() === "All" || getSelectedCount() > 1 ? "s" : ""
              } selected`}
            </div>
          </div>
          <span className="closeIcon">
            <i className="material-icons close" onClick={closePopup}>
              close
            </i>
          </span>
        </div>
        <div className="cardBody">
          <div className="emailAndInputBoxCont">
            <InputTextBox
              name="changeContactAccountInput"
              id="changeContactAccountInput"
              type="text"
              value={accountInput}
              change={onAccountInputChange}
              onFocus={onAccountInputFocus}
              placeHolder="Search for an account by name or domain"
            />
            <AccountAutoCompleteDropdown
              inputText={accountInput}
              selectedCallbk={onAccountSelected}
              focusTrigger={focusTrigger}
              onCreateNew={openCreateAccountPopup}
            />
          </div>
          <div className="buttonActionList">
            {buttonList.map(item => (
              <Button {...item} key={`${item?.id}Btn`} />
            ))}
          </div>
        </div>
      </div>
      <div className="bgOverlay" />
    </div>
  );
});

export default ChangeContactAccountPopup;
export { ChangeContactAccountPopup };
